// Complete corrected code with all variables defined

import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "../Styling/Amazon_OrdersTable.css";
import { Base_URL } from "../url";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZE = 50;

// Updated API endpoint for filtered data
const API_BASE_URL = `${Base_URL}/Amazon_MTR/filtered-non-shipment`;

/* ------------------ Constants for Filters ------------------ */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const TRANSACTION_TYPES = ["Cancel", "Refund", "Shipment", "FreeReplacement"];

// Generate dynamic year range (last 6 years + current + next 6 years)
const generateYearRange = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 6; i <= currentYear + 6; i++) {
    years.push(i);
  }
  return years;
};

// Helper function to convert month name to number (1-12)
const getMonthNumber = (monthName) => {
  return MONTHS.indexOf(monthName) + 1;
};

// Helper function to convert month name to 3-letter abbreviation
const getMonthAbbreviation = (monthName) => {
  const monthMap = {
    January: "Jan",
    February: "Feb",
    March: "Mar",
    April: "Apr",
    May: "May",
    June: "Jun",
    July: "Jul",
    August: "Aug",
    September: "Sep",
    October: "Oct",
    November: "Nov",
    December: "Dec",
  };
  return monthMap[monthName] || monthName;
};

/* ------------------ Loader Row ------------------ */
const TableLoader = () => (
  <tr>
    <td colSpan="10" className="amazon-order-table-loader">
      <div className="amazon-order-loader-card">
        <div className="amazon-order-loader-icon">📅</div>
        <div className="amazon-order-loader-text">Loading orders…</div>
      </div>
    </td>
  </tr>
);

const MahOrdersTable = () => {
  // Data from API (current page only)
  const [allRows, setAllRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Selection state - stores IDs and actual row data across ALL pages
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [selectedRowsData, setSelectedRowsData] = useState(new Map());

  /* ------------------ Filter States ------------------ */
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedTransactionType, setSelectedTransactionType] = useState("");

  // Generate years dynamically
  const years = useMemo(() => generateYearRange(), []);

  /* ------------------ Check if filters are selected ------------------ */
  const areFiltersSelected =
    selectedYear && selectedTransactionType && selectedMonth !== "";

  /* ------------------ Fetch Orders from API with Filters ------------------ */
  const fetchOrders = async (page) => {
    if (!areFiltersSelected) return;

    setLoading(true);
    try {
      const monthNumber = getMonthNumber(selectedMonth);

      const response = await axios.get(API_BASE_URL, {
        params: {
          page,
          pageSize: PAGE_SIZE,
          month: monthNumber,
          year: selectedYear,
          transactionType: selectedTransactionType,
        },
      });

      // console.log("API Response:", response.data);

      const rawData = response?.data?.data || [];
      const apiTotalPages = response?.data?.totalPages || 1;
      const apiTotalRecords = response?.data?.totalRecords || 0;

      // Normalize backend → frontend
      const normalizedRows = rawData.map((item, index) => ({
        // ✅ Composite key: unique per row even if orderId repeats
        // Added page number to avoid key collision across pages
        RowKey: `${item.orderId}_${item.sku}_${item.shipmentId ?? "NA"}_${page}_${index}`,
        OrderId: item.orderId,
        TransactionType: item.transactionType,
        ShipmentId: item.shipmentId,
        ShipmentDate: item.shipmentDate,
        Quantity: item.quantity,
        SKU: item.sku,
        InvoiceDate: item.invoiceDate,
        OrderDate: item.orderDate,
        InvoiceNumber: item.invoiceNumber,
        WarehouseId: item.warehouseId,
        ShipToState: item.shipToState,
        TaxExclusiveGross: item.taxExclusiveGross,
        TotalTaxAmount: item.totalTaxAmount,
        CgstRate: item.cgstRate,
        SgstRate: item.sgstRate,
        IgstRate: item.igstRate,
        ShippingAmount: item.shippingAmount,
        ShippingAmountBasis: item.shippingAmountBasis,
      }));

      setAllRows(normalizedRows);
      setTotalPages(apiTotalPages);
      setTotalRecords(apiTotalRecords);
    } catch (error) {
      // console.error("Failed to fetch orders", error);
      setAllRows([]);
      setTotalPages(0);
      setTotalRecords(0);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ Fetch data when filters change ------------------ */
  useEffect(() => {
    if (areFiltersSelected) {
      setCurrentPage(1);
      setSelectedOrderIds(new Set());
      setSelectedRowsData(new Map());
      fetchOrders(1);
    } else {
      setAllRows([]);
      setTotalPages(0);
      setTotalRecords(0);
    }
  }, [selectedMonth, selectedYear, selectedTransactionType]);

  /* ------------------ Fetch data when page changes ------------------ */
  useEffect(() => {
    if (areFiltersSelected && currentPage >= 1) {
      fetchOrders(currentPage);
    }
  }, [currentPage]);

  /* ------------------ Checkbox Logic ------------------ */
  const currentPageOrderIds = useMemo(
    () => allRows.map((r) => r.RowKey),
    [allRows]
  );

  const allChecked =
    currentPageOrderIds.length > 0 &&
    currentPageOrderIds.every((id) => selectedOrderIds.has(id));

  const someChecked =
    currentPageOrderIds.some((id) => selectedOrderIds.has(id)) && !allChecked;

  const toggleSelectAll = () => {
    setSelectedOrderIds((prev) => {
      const updated = new Set(prev);

      if (allChecked) {
        // Deselect all on current page
        currentPageOrderIds.forEach((id) => {
          updated.delete(id);
        });
      } else {
        // Select all on current page
        currentPageOrderIds.forEach((id) => {
          updated.add(id);
        });
      }

      return updated;
    });

    // Update selectedRowsData Map
    setSelectedRowsData((prevData) => {
      const newData = new Map(prevData);

      if (allChecked) {
        // Remove current page rows from Map
        currentPageOrderIds.forEach((id) => {
          newData.delete(id);
        });
      } else {
        // Add current page rows to Map
        allRows.forEach((row) => {
          newData.set(row.RowKey, row);
        });
      }

      return newData;
    });
  };

  // ✅ FIXED: Was using `orderId` as parameter name but receiving a RowKey,
  //           then searching by r.OrderId which would never match a RowKey string.
  //           Now correctly named `rowKey` and searches by r.RowKey.
  const toggleRow = (rowKey) => {
    setSelectedOrderIds((prev) => {
      const updated = new Set(prev);

      if (updated.has(rowKey)) {
        updated.delete(rowKey);
      } else {
        updated.add(rowKey);
      }

      return updated;
    });

    // Update selectedRowsData Map
    setSelectedRowsData((prevData) => {
      const newData = new Map(prevData);

      if (newData.has(rowKey)) {
        // Remove from Map
        newData.delete(rowKey);
      } else {
        // ✅ FIXED: Search by r.RowKey instead of r.OrderId
        //           Previously: allRows.find((r) => r.OrderId === orderId)
        //           This always returned undefined because orderId was actually
        //           a RowKey string, making the payload empty on Push to BC.
        const rowData = allRows.find((r) => r.RowKey === rowKey);
        if (rowData) {
          newData.set(rowKey, rowData);
        }
      }

      return newData;
    });
  };

  /* ------------------ Reset Filters ------------------ */
  const resetFilters = () => {
    setSelectedMonth("");
    setSelectedYear("");
    setSelectedTransactionType("");
    setAllRows([]);
    setCurrentPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    setSelectedOrderIds(new Set());
    setSelectedRowsData(new Map());
  };

  /* ------------------ Pagination Handlers ------------------ */
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  };

  /* ------------------ Pagination Display Info ------------------ */
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalRecords);

  /* ------------------ Bulk Action Handler ------------------ */
  const handleBulkAction = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (loading) {
      // console.log("Push to BC already in progress, ignoring duplicate call");
      return;
    }

    if (selectedOrderIds.size === 0) {
      toast.warn("No orders selected");
      return;
    }

    setLoading(true);

    // Get selected rows data from the Map (across ALL pages)
    const selectedRows = Array.from(selectedRowsData.values());

    // console.log("Selected Rows (from all pages):", selectedRows);
    // console.log("Total selected count:", selectedRows.length);

    // Build products array from selected rows
    const products = selectedRows.map((row) => ({
      transactionType: row.TransactionType,
      orderId: row.OrderId,
      shipmentId: row.ShipmentId,
      shipmentDate: row.ShipmentDate,
      quantity: row.Quantity,
      sku: row.SKU,
      invoiceDate: row.InvoiceDate,
      orderDate: row.OrderDate,
      invoiceNumber: row.InvoiceNumber,
      taxExclusiveGross: row.TaxExclusiveGross,
      totalTaxAmount: row.TotalTaxAmount,
      CGST_rate: row.CgstRate,
      SGST_rate: row.SgstRate,
      IGST_rate: row.IgstRate,
      ShippingAmount: row.ShippingAmount,
      ShippingAmountBasis: row.ShippingAmountBasis,
    }));

    // Convert month name to 3-letter abbreviation
    const monthAbbr = getMonthAbbreviation(selectedMonth);

    // Build payload
    const payload = {
      year: String(selectedYear),
      month: monthAbbr,
      transactionType: selectedTransactionType,
      products: products,
    };

    // console.log("Push to BC Payload:", payload);

    try {
      const response = await axios.post(
        `${Base_URL}/Amazon_Screen_Request/SaveAmazonMtrRequest/v3`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      // console.log("Push to BC Response:", response.data);

      if (response.data.success) {
        toast.success(
  `Push to BC completed! Request ID: ${response.data.requestId} • ${selectedOrderIds.size} orders processed`
);

        // Clear selections
        setSelectedOrderIds(new Set());
        setSelectedRowsData(new Map());

        // Refetch current page to remove posted data
        await fetchOrders(currentPage);

        // If current page becomes empty and not first page, go back
        if (currentPage > 1 && allRows.length === 0) {
          setCurrentPage((prev) => prev - 1);
        }
      } else {
        toast.warn(`Action completed but with issues: ${response.data.message}`);
      }
    } catch (error) {
      // console.error("Push to BC failed:", error);

      let errorMessage = "Push to BC failed. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <div className="amazon-order-page">
      <ToastContainer position="top-right" autoClose={4000}/>
      <div className="amazon-order-header">
        <h2 className="amazon-order-title">Orders</h2>
        <p className="amazon-order-subtitle">
          Manage and review customer orders
        </p>
      </div>

      {/* Filter Section */}
      <div className="amazon-order-table-filters-section">
        <div className="amazon-order-table-filters-container">
          {/* Month Filter */}
          <div className="amazon-order-table-filter-group">
            <label htmlFor="month-filter">Month</label>
            <select
              id="month-filter"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="amazon-order-table-filter-select"
              disabled={loading}
            >
              <option value="">Select Month</option>
              {MONTHS.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div className="amazon-order-table-filter-group">
            <label htmlFor="year-filter">Year</label>
            <select
              id="year-filter"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="amazon-order-table-filter-select"
              disabled={loading}
            >
              <option value="">Select Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Transaction Type Filter */}
          <div className="amazon-order-table-filter-group">
            <label htmlFor="transaction-filter">Transaction Type</label>
            <select
              id="transaction-filter"
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              className="amazon-order-table-filter-select"
              disabled={loading}
            >
              <option value="">Select Type</option>
              {TRANSACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Reset Button */}
          <div className="amazon-order-table-filter-group">
            <label>&nbsp;</label>
            <button
              onClick={resetFilters}
              className="amazon-order-table-reset-btn"
              disabled={
                !selectedMonth && !selectedYear && !selectedTransactionType
              }
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Filter Warning */}
        {!areFiltersSelected && (
          <div className="amazon-order-table-filter-warning">
            ⚠️ Please select all three filters (Month, Year, and Transaction
            Type) to view orders
          </div>
        )}
      </div>

      <div className="amazon-order-table-actions">
        <span className="amazon-order-table-selected-count">
          Selected: <b>{selectedOrderIds.size}</b>
        </span>

        <button
          className="amazon-order-table-bulk-action"
          disabled={selectedOrderIds.size === 0 || loading}
          onClick={handleBulkAction}
        >
          {loading ? "Processing..." : "Push to BC"}
        </button>
      </div>

      <div className="amazon-order-table-table-card">
        <div className="amazon-order-table-table-body">
          <table className="amazon-order-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked;
                    }}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Order ID</th>
                <th>Transaction Type</th>
                <th>Shipment ID</th>
                <th>Shipment Date</th>
                <th>Quantity</th>
                <th>SKU</th>
                <th>Invoice Date</th>
                <th>Order Date</th>
                <th>Invoice Number</th>
              </tr>
            </thead>

            <tbody>
              {!areFiltersSelected ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    Please select all filters to view data
                  </td>
                </tr>
              ) : loading ? (
                <TableLoader />
              ) : allRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    No data found for selected filters
                  </td>
                </tr>
              ) : (
                allRows.map((row) => (
                  <tr key={row.RowKey}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.has(row.RowKey)}
                        onChange={() => toggleRow(row.RowKey)}
                      />
                    </td>
                    <td>{row.OrderId}</td>
                    <td>{row.TransactionType}</td>
                    <td>{row.ShipmentId || "-"}</td>
                    <td>{row.ShipmentDate || "-"}</td>
                    <td>{row.Quantity}</td>
                    <td>{row.SKU}</td>
                    <td>{row.InvoiceDate}</td>
                    <td>{row.OrderDate || "-"}</td>
                    <td>{row.InvoiceNumber}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {areFiltersSelected && totalRecords > 0 && (
          <div className="amazon-order-table-pagination">
            <span className="amazon-order-table-pagination-info">
              Page {currentPage} of {totalPages} • Showing {startIndex}-
              {endIndex} of {totalRecords} orders
            </span>

            <div className="amazon-order-table-pagination-controls">
              <button
                disabled={currentPage === 1 || loading}
                onClick={goToPreviousPage}
              >
                Prev
              </button>

              <span className="amazon-order-table-page-number">
                {currentPage}
              </span>

              <button
                disabled={currentPage >= totalPages || loading}
                onClick={goToNextPage}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MahOrdersTable;